import { inject, computed } from "@angular/core";
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
    withHooks,
} from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tap, pipe, filter, switchMap, Observable, iif } from "rxjs";
import {
    withRequestStatus,
    setPending,
    setFulfilled,
    setError,
} from "../extensions/request-status.feature";
import { withRouteParams } from "../extensions/route-params.feature";
import { AchievementsStore } from "./achievements-store";
import { ApiAchievementsService } from "../../services/api-achievements.service";
import { AchievementState } from "../../models/achievements/achievement-state";
import { AchievementEditModel } from "@playcharge/common/models/backend/achievements/backoffice/achievement-edit-model";
import { AchievementBackofficeViewModel } from "@playcharge/common/models/backend/achievements/backoffice/achievement-backoffice-view-model";

export type AchievementStore = InstanceType<typeof AchievementStore>;

export const AchievementStore = signalStore(
    { protectedState: false },
    withState<AchievementState>({
        achievementId: 0,
    }),
    withRouteParams({ achievementId: (param) => Number(param) }),
    withRequestStatus(),
    withComputed(({ achievementId }, achievementStore = inject(AchievementsStore)) => ({
        achievement: computed(() =>
            achievementId() ? achievementStore.entityMap()[achievementId()] : null,
        ),
    })),
    withMethods(
        (
            store,
            achievementsStore = inject(AchievementsStore),
            apiAchievementsService = inject(ApiAchievementsService),
        ) => ({
            createOrUpdateAchievement(
                model: AchievementEditModel,
            ): Observable<AchievementBackofficeViewModel> {
                return apiAchievementsService
                    .createOrUpdate(model)
                    .pipe(tap((achievement) => achievementsStore.setAchievement(achievement)));
            },
            loadAchievement: rxMethod<number>(
                pipe(
                    filter((id) => !!Number(id) && !store.achievement()),
                    tap(() => patchState(store, setPending())),
                    switchMap((id) => {
                        return apiAchievementsService.getById(id).pipe(
                            tap({
                                next: (achievement) => {
                                    patchState(store, setFulfilled());

                                    achievementsStore.setAchievement(achievement);
                                },
                                error: (error) => patchState(store, setError(error.error.error)),
                            }),
                        );
                    }),
                ),
            ),
        }),
    ),
    withHooks({
        onInit({ loadAchievement, achievementId }) {
            loadAchievement(achievementId);
        },
    }),
);
